import React from 'react';
import { Droplets, Cookie, GlassWater, Utensils, Snowflake, AlertTriangle } from 'lucide-react';

export const ServingSuggestionsPage = () => {
  return (
    <div className="paper-texture min-h-screen">
      {/* Header */}
      <div className="bg-surface py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-xl text-primary">How to Enjoy</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-secondary mt-2" data-testid="serving-title">
            Fire Cider – Serving Suggestions
          </h1>
          <p className="text-muted-foreground mt-4 text-lg font-medium">
            Shake well before use.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-12">
          
          {/* Daily Wellness Shot */}
          <section className="bg-white rounded-xl border p-8 hover:shadow-warm-lg transition-shadow" data-testid="section-wellness">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🥄</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-secondary">Daily Wellness Shot</h2>
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Take 1 tablespoon (15ml) once or twice daily
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Best taken before meals
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Can be taken straight or diluted
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Diluted Drink */}
          <section className="bg-white rounded-xl border p-8 hover:shadow-warm-lg transition-shadow" data-testid="section-diluted">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">💧</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-secondary">Diluted Drink</h2>
                <p className="mt-4 text-muted-foreground">Mix 1 tablespoon with:</p>
                <ul className="mt-3 space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Warm water
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Hot (not boiling) water
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Cold water or sparkling water
                  </li>
                </ul>
                <p className="mt-4 text-muted-foreground italic">
                  Add honey or maple syrup to taste if desired
                </p>
              </div>
            </div>
          </section>

          {/* With Honey */}
          <section className="bg-white rounded-xl border p-8 hover:shadow-warm-lg transition-shadow" data-testid="section-honey">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🍯</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-secondary">With Honey</h2>
                <p className="mt-4 text-muted-foreground">Stir 1 tablespoon of fire cider into:</p>
                <ul className="mt-3 space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Raw honey
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Warm lemon water with honey
                  </li>
                </ul>
                <p className="mt-4 text-muted-foreground italic">
                  Ideal for a gentler taste
                </p>
              </div>
            </div>
          </section>

          {/* In Drinks & Food */}
          <section className="bg-white rounded-xl border p-8 hover:shadow-warm-lg transition-shadow" data-testid="section-food">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🍹</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-secondary">In Drinks & Food</h2>
                <p className="mt-4 text-muted-foreground">Add a splash to:</p>
                <ul className="mt-3 space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Salad dressings & vinaigrettes
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Marinades for vegetables or meats
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Bloody Marys or savoury mocktails
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    Herbal teas
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Seasonal Use */}
          <section className="bg-white rounded-xl border p-8 hover:shadow-warm-lg transition-shadow" data-testid="section-seasonal">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">❄️</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-secondary">Seasonal Use</h2>
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    Can be enjoyed daily, particularly during colder months
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    Many customers enjoy it as part of a morning or evening ritual
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Notes for Use */}
          <section className="bg-amber-50 rounded-xl border border-amber-200 p-8" data-testid="section-notes">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-secondary">Notes for Use</h2>
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    Start with a small amount if new to fire cider
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    Avoid if sensitive to strong vinegar or spices
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    Not recommended during pregnancy without professional advice
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    Dental note: Due to natural acidity, dilute before drinking or rinse mouth with water after use
                  </li>
                </ul>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};
